import React, { useState, useEffect } from "react";
import UserSettingsAPI from "../../services/UserSettingsAPI";
import ConsentAPI from "../../services/ConsentAPI";
import { useRoleCheck } from "../../../../hooks/useRoleCheck";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faShieldAlt } from "@fortawesome/free-solid-svg-icons";

const PrivacyCommon = () => {
  const { t } = useTranslation();
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [emailMarketing, setEmailMarketing] = useState(false);
  const [profileVisibility, setProfileVisibility] = useState("team");

  // Owner access consent state (for non-owner member users only)
  const { isAccountAdmin } = useRoleCheck();
  const [dashboardConsent, setDashboardConsent] = useState(false);
  const [analysisConsent, setAnalysisConsent] = useState(false);
  const [reportsConsent, setReportsConsent] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentError, setConsentError] = useState(null);
  const isMemberUser = !isAccountAdmin();

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await UserSettingsAPI.getSettings();
        if (data) {
          setAnalyticsEnabled(data.analyticsEnabled ?? true);
          setEmailMarketing(data.emailMarketing ?? false);
          setProfileVisibility(data.profileVisibility || "team");
        }
      } catch (err) {
        console.error("PrivacySettings: Error loading settings", err);
        setAnalyticsEnabled(
          localStorage.getItem("analyticsEnabled") !== "false",
        );
        setEmailMarketing(localStorage.getItem("emailMarketing") === "true");
        setProfileVisibility(
          localStorage.getItem("profileVisibility") || "team",
        );
      }
    };
    loadSettings();
  }, []);

  // Load owner access consent status for member users
  useEffect(() => {
    if (!isMemberUser) return;
    const loadConsent = async () => {
      try {
        setConsentLoading(true);
        const data = await ConsentAPI.getMyOwnerAccessConsent();
        if (data) {
          setDashboardConsent(data.dashboardAccess ?? false);
          setAnalysisConsent(data.analysisAccess ?? false);
          setReportsConsent(data.reportsAccess ?? false);
        }
      } catch (err) {
        console.error(
          "PrivacySettings: Error loading owner access consent",
          err,
        );
        setConsentError(t("privacy.ownerAccess.error.load"));
      } finally {
        setConsentLoading(false);
      }
    };
    loadConsent();
  }, [isMemberUser]);

  const handleConsentToggle = async (consentType, currentValue, setter) => {
    const newValue = !currentValue;
    setter(newValue);
    setConsentError(null);
    try {
      await ConsentAPI.updateOwnerAccessConsent(consentType, newValue);
    } catch (err) {
      console.error(`Failed to update ${consentType}`, err);
      setter(currentValue);
      setConsentError(t("privacy.ownerAccess.error.save"));
    }
  };

  const handleToggle = async (field, currentValue, setter) => {
    const newValue = !currentValue;
    setter(newValue);
    try {
      await UserSettingsAPI.updateSettings({ [field]: newValue });
    } catch (err) {
      console.error(`Failed to save ${field}`, err);
      setter(currentValue);
    }
  };

  const handleProfileVisibilityChange = async (e) => {
    const newValue = e.target.value;
    const oldValue = profileVisibility;
    setProfileVisibility(newValue);
    try {
      await UserSettingsAPI.updateSettings({ profileVisibility: newValue });
    } catch (err) {
      console.error("Failed to save profileVisibility", err);
      setProfileVisibility(oldValue);
    }
  };

  return (
    <>
      {isMemberUser && (
        <div className="common-form-group">
          <h4>
            <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 8 }} />
            {t("privacy.ownerAccess.heading")}
          </h4>
          <p
            style={{
              color: "var(--text-secondary, #888)",
              marginBottom: 12,
              fontSize: "0.9em",
            }}
          >
            {t("privacy.ownerAccess.desc")}
          </p>
          {consentError && (
            <div className="common-error-message" style={{ marginBottom: 12 }}>
              {consentError}
            </div>
          )}
          <div className="common-toggle-group indented-below-h4">
            <div className="common-toggle-info">
              <h4>
                <FontAwesomeIcon icon={faEye} style={{ marginRight: 6 }} />
                {t("privacy.ownerAccess.dashboard.title")}
              </h4>
              <p>{t("privacy.ownerAccess.dashboard.desc")}</p>
            </div>
            <div
              className={`common-toggle ${dashboardConsent ? "active" : ""}`}
              onClick={() =>
                !consentLoading &&
                handleConsentToggle(
                  "owner_dashboard_access",
                  dashboardConsent,
                  setDashboardConsent,
                )
              }
              role="switch"
              aria-checked={dashboardConsent}
              aria-label={t("privacy.ownerAccess.dashboard.ariaLabel")}
              style={
                consentLoading
                  ? { opacity: 0.5, pointerEvents: "none" }
                  : undefined
              }
            ></div>
          </div>
          <div className="common-toggle-group indented-below-h4">
            <div className="common-toggle-info">
              <h4>
                <FontAwesomeIcon icon={faEye} style={{ marginRight: 6 }} />
                {t("privacy.ownerAccess.analysis.title")}
              </h4>
              <p>{t("privacy.ownerAccess.analysis.desc")}</p>
            </div>
            <div
              className={`common-toggle ${analysisConsent ? "active" : ""}`}
              onClick={() =>
                !consentLoading &&
                handleConsentToggle(
                  "owner_analysis_access",
                  analysisConsent,
                  setAnalysisConsent,
                )
              }
              role="switch"
              aria-checked={analysisConsent}
              aria-label={t("privacy.ownerAccess.analysis.ariaLabel")}
              style={
                consentLoading
                  ? { opacity: 0.5, pointerEvents: "none" }
                  : undefined
              }
            ></div>
          </div>
          <div className="common-toggle-group indented-below-h4">
            <div className="common-toggle-info">
              <h4>
                <FontAwesomeIcon icon={faEye} style={{ marginRight: 6 }} />
                {t("privacy.ownerAccess.reports.title")}
              </h4>
              <p>{t("privacy.ownerAccess.reports.desc")}</p>
            </div>
            <div
              className={`common-toggle ${reportsConsent ? "active" : ""}`}
              onClick={() =>
                !consentLoading &&
                handleConsentToggle(
                  "owner_reports_access",
                  reportsConsent,
                  setReportsConsent,
                )
              }
              role="switch"
              aria-checked={reportsConsent}
              aria-label={t("privacy.ownerAccess.reports.ariaLabel")}
              style={
                consentLoading
                  ? { opacity: 0.5, pointerEvents: "none" }
                  : undefined
              }
            ></div>
          </div>
        </div>
      )}
      <div className="common-form-group">
        <h4>{t("privacy.dataSharing.heading")}</h4>
        <div className="common-toggle-group indented-below-h4">
          <div className="common-toggle-info">
            <h4>{t("privacy.dataSharing.analytics.title")}</h4>
            <p>{t("privacy.dataSharing.analytics.desc")}</p>
          </div>
          <div
            className={`common-toggle ${analyticsEnabled ? "active" : ""}`}
            onClick={() =>
              handleToggle(
                "analyticsEnabled",
                analyticsEnabled,
                setAnalyticsEnabled,
              )
            }
            role="switch"
            aria-checked={analyticsEnabled}
          ></div>
        </div>
        <div className="common-toggle-group indented-below-h4">
          <div className="common-toggle-info">
            <h4>{t("privacy.dataSharing.marketing.title")}</h4>
            <p>{t("privacy.dataSharing.marketing.desc")}</p>
          </div>
          <div
            className={`common-toggle ${emailMarketing ? "active" : ""}`}
            onClick={() =>
              handleToggle("emailMarketing", emailMarketing, setEmailMarketing)
            }
            role="switch"
            aria-checked={emailMarketing}
          ></div>
        </div>
      </div>
      <div className="common-form-group">
        <h4>{t("privacy.profileVisibility.heading")}</h4>
        <div className="common-form-row">
          <div className="common-form-field">
            <label>{t("privacy.profileVisibility.label")}</label>
            <select
              value={profileVisibility}
              onChange={handleProfileVisibilityChange}
            >
              <option value="private">
                {t("privacy.profileVisibility.private")}
              </option>
              <option value="team">
                {t("privacy.profileVisibility.team")}
              </option>
              <option value="public">
                {t("privacy.profileVisibility.public")}
              </option>
            </select>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyCommon;
